// src/pages/legal/CookiesPolicy.js
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  Divider,
  List,
  ListItem,
  ListItemText,
  Alert,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Cookie as CookieIcon,
  Settings as SettingsIcon,
  Analytics as AnalyticsIcon,
  Security as SecurityIcon,
  Tune as TuneIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon
} from '@mui/icons-material';

// Paleta de colores verde pastel
const Colors = {
  primary: '#7FB069',
  primaryLight: '#A8D5BA',
  primaryDark: '#6B8E5A',
  secondary: '#000000',
  accent: '#90C695',
  background: '#F0F8F0',
  surface: '#FFFFFF',
  text: '#000000',
  textSecondary: '#724CA7'
};

const CookiesPolicy = () => {
  const [cookiePreferences, setCookiePreferences] = useState({
    technical: true, // Always enabled
    functional: true,
    analytics: false,
    marketing: false
  });

  const handlePreferenceChange = (type) => (event) => {
    if (type === 'technical') return; // Technical cookies cannot be disabled
    setCookiePreferences(prev => ({
      ...prev,
      [type]: event.target.checked
    }));
  };

  const handleAcceptAll = () => {
    setCookiePreferences({
      technical: true,
      functional: true,
      analytics: true,
      marketing: true
    });
  };

  const handleRejectAll = () => {
    setCookiePreferences({
      technical: true,
      functional: false,
      analytics: false,
      marketing: false
    });
  };

  const handleSavePreferences = () => {
    // Here you would implement the actual cookie setting logic
    console.log('Cookie preferences saved:', cookiePreferences);
  };

  return (
    <Box sx={{ flexGrow: 1, bgcolor: Colors.background, minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: Colors.surface, border: `2px solid ${Colors.primaryLight}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CookieIcon sx={{ fontSize: 40, color: Colors.primary, mr: 2 }} />
            <Typography variant="h3" sx={{ color: Colors.text, fontWeight: 'bold' }}>
              Política de Cookies
            </Typography>
          </Box>
          <Typography variant="h6" color={Colors.textSecondary} paragraph>
            AMPA del Bosc - Información sobre el uso de Cookies
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Fecha de última actualización:</strong> {new Date().toLocaleDateString('es-ES')}
            </Typography>
          </Alert>
        </Paper>

        {/* Cookie Preferences Panel */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: Colors.surface, border: `2px solid ${Colors.accent}` }}>
          <Typography variant="h4" gutterBottom sx={{ color: Colors.primary, display: 'flex', alignItems: 'center' }}>
            <TuneIcon sx={{ mr: 2 }} />
            Configuración de Cookies
          </Typography>
          <Divider sx={{ mb: 3, borderColor: Colors.primaryLight }} />

          <Typography paragraph>
            Gestione sus preferencias de cookies. Puede cambiar estas configuraciones en cualquier momento.
          </Typography>

          <Stack spacing={3}>
            <Paper sx={{ p: 3, bgcolor: Colors.background }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h6" gutterBottom color={Colors.primaryDark}>
                    🔧 Cookies Técnicas (Obligatorias)
                  </Typography>
                  <Typography variant="body2" color={Colors.textSecondary}>
                    Necesarias para el funcionamiento básico del sitio web. No se pueden desactivar.
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={cookiePreferences.technical}
                      disabled={true}
                      color="primary"
                    />
                  }
                  label="Activadas"
                />
              </Box>
            </Paper>

            <Paper sx={{ p: 3, bgcolor: Colors.background }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h6" gutterBottom color={Colors.primaryDark}>
                    ⚙️ Cookies Funcionales
                  </Typography>
                  <Typography variant="body2" color={Colors.textSecondary}>
                    Mejoran la funcionalidad y personalización, como recordar sus preferencias.
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={cookiePreferences.functional}
                      onChange={handlePreferenceChange('functional')}
                      color="primary"
                    />
                  }
                  label={cookiePreferences.functional ? "Activadas" : "Desactivadas"}
                />
              </Box>
            </Paper>

            <Paper sx={{ p: 3, bgcolor: Colors.background }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h6" gutterBottom color={Colors.primaryDark}>
                    📊 Cookies Analíticas
                  </Typography>
                  <Typography variant="body2" color={Colors.textSecondary}>
                    Nos ayudan a entender cómo interactúa con el sitio web (Google Analytics).
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={cookiePreferences.analytics}
                      onChange={handlePreferenceChange('analytics')}
                      color="primary"
                    />
                  }
                  label={cookiePreferences.analytics ? "Activadas" : "Desactivadas"}
                />
              </Box>
            </Paper>

            <Paper sx={{ p: 3, bgcolor: Colors.background }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h6" gutterBottom color={Colors.primaryDark}>
                    📢 Cookies de Marketing
                  </Typography>
                  <Typography variant="body2" color={Colors.textSecondary}>
                    Se utilizan para mostrar anuncios más relevantes (actualmente no utilizadas).
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={cookiePreferences.marketing}
                      onChange={handlePreferenceChange('marketing')}
                      color="primary"
                      disabled={true}
                    />
                  }
                  label="No utilizadas"
                />
              </Box>
            </Paper>
          </Stack>

          <Box sx={{ display: 'flex', gap: 2, mt: 4, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              onClick={handleAcceptAll}
              sx={{
                bgcolor: Colors.primary,
                '&:hover': { bgcolor: Colors.primaryDark }
              }}
            >
              Aceptar Todas
            </Button>
            <Button
              variant="outlined"
              onClick={handleRejectAll}
              sx={{
                borderColor: Colors.primary,
                color: Colors.primary,
                '&:hover': { borderColor: Colors.primaryDark }
              }}
            >
              Rechazar Opcionales
            </Button>
            <Button
              variant="contained"
              onClick={handleSavePreferences}
              sx={{
                bgcolor: Colors.accent,
                '&:hover': { bgcolor: Colors.primaryDark }
              }}
            >
              Guardar Preferencias
            </Button>
          </Box>
        </Paper>

        {/* Qué son las Cookies */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: Colors.surface, border: `1px solid ${Colors.primaryLight}` }}>
          <Typography variant="h4" gutterBottom sx={{ color: Colors.primary, display: 'flex', alignItems: 'center' }}>
            <InfoIcon sx={{ mr: 2 }} />
            1. ¿Qué son las Cookies?
          </Typography>
          <Divider sx={{ mb: 3, borderColor: Colors.primaryLight }} />

          <Typography paragraph>
            Las cookies son pequeños archivos de texto que se almacenan en su dispositivo cuando visita un sitio web. Son ampliamente utilizadas para hacer que los sitios web funcionen de manera más eficiente, así como para proporcionar información a los propietarios del sitio.
          </Typography>

          <Box sx={{ bgcolor: Colors.background, p: 3, borderRadius: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom color={Colors.primaryDark}>
              Características de las Cookies
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="• No contienen información personal identificable por sí mismas"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• No pueden ejecutar códigos ni transmitir virus"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Tienen fecha de caducidad después de la cual se eliminan automáticamente"
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="• Pueden ser eliminadas por el usuario en cualquier momento"
                />
              </ListItem>
            </List>
          </Box>
        </Paper>

        {/* Tipos de Cookies que Utilizamos */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: Colors.surface, border: `1px solid ${Colors.primaryLight}` }}>
          <Typography variant="h4" gutterBottom sx={{ color: Colors.primary, display: 'flex', alignItems: 'center' }}>
            <SettingsIcon sx={{ mr: 2 }} />
            2. Tipos de Cookies que Utilizamos
          </Typography>
          <Divider sx={{ mb: 3, borderColor: Colors.primaryLight }} />

          <Stack spacing={3}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <SecurityIcon sx={{ mr: 2, color: Colors.primary }} />
                  <Typography variant="h6" color={Colors.primaryDark}>
                    Cookies Técnicas (Estrictamente Necesarias)
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography paragraph>
                  Estas cookies son esenciales para el funcionamiento del sitio web y no se pueden desactivar en nuestros sistemas.
                </Typography>
                <TableContainer component={Paper} sx={{ bgcolor: Colors.background }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Nombre</strong></TableCell>
                        <TableCell><strong>Propósito</strong></TableCell>
                        <TableCell><strong>Duración</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>PHPSESSID</TableCell>
                        <TableCell>Identificador de sesión del usuario</TableCell>
                        <TableCell>Sesión</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>csrf_token</TableCell>
                        <TableCell>Protección contra ataques CSRF</TableCell>
                        <TableCell>Sesión</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>cookie_consent</TableCell>
                        <TableCell>Recordar preferencias de cookies</TableCell>
                        <TableCell>1 año</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TuneIcon sx={{ mr: 2, color: Colors.accent }} />
                  <Typography variant="h6" color={Colors.primaryDark}>
                    Cookies Funcionales
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography paragraph>
                  Estas cookies permiten que el sitio web proporcione una funcionalidad y personalización mejoradas.
                </Typography>
                <TableContainer component={Paper} sx={{ bgcolor: Colors.background }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Nombre</strong></TableCell>
                        <TableCell><strong>Propósito</strong></TableCell>
                        <TableCell><strong>Duración</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>user_preferences</TableCell>
                        <TableCell>Configuraciones personalizadas del usuario</TableCell>
                        <TableCell>30 días</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>language</TableCell>
                        <TableCell>Idioma preferido del usuario</TableCell>
                        <TableCell>1 año</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>theme_mode</TableCell>
                        <TableCell>Modo claro/oscuro seleccionado</TableCell>
                        <TableCell>1 año</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AnalyticsIcon sx={{ mr: 2, color: Colors.textSecondary }} />
                  <Typography variant="h6" color={Colors.primaryDark}>
                    Cookies Analíticas
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography paragraph>
                  Estas cookies nos permiten analizar el uso del sitio web para mejorarlo.
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Google Analytics:</strong> Utilizamos Google Analytics para entender cómo los visitantes usan nuestro sitio web. Los datos son anónimos y agregados.
                  </Typography>
                </Alert>
                <TableContainer component={Paper} sx={{ bgcolor: Colors.background }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Nombre</strong></TableCell>
                        <TableCell><strong>Propósito</strong></TableCell>
                        <TableCell><strong>Duración</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>_ga</TableCell>
                        <TableCell>Distinguir usuarios únicos</TableCell>
                        <TableCell>2 años</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>_ga_XXXXXXXXXX</TableCell>
                        <TableCell>Estado de la sesión de Google Analytics</TableCell>
                        <TableCell>2 años</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>_gid</TableCell>
                        <TableCell>Distinguir usuarios únicos</TableCell>
                        <TableCell>24 horas</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          </Stack>
        </Paper>

        {/* Cookies de Terceros */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: Colors.surface, border: `1px solid ${Colors.primaryLight}` }}>
          <Typography variant="h4" gutterBottom sx={{ color: Colors.primary, display: 'flex', alignItems: 'center' }}>
            <AnalyticsIcon sx={{ mr: 2 }} />
            3. Cookies de Terceros
          </Typography>
          <Divider sx={{ mb: 3, borderColor: Colors.primaryLight }} />

          <Typography paragraph>
            Nuestro sitio web puede utilizar servicios de terceros que establecen sus propias cookies:
          </Typography>

          <Stack spacing={2}>
            <Paper sx={{ p: 3, bgcolor: Colors.background }}>
              <Typography variant="h6" gutterBottom color={Colors.primaryDark}>
                📊 Google Analytics
              </Typography>
              <Typography variant="body2" paragraph>
                Utilizamos Google Analytics para analizar el tráfico de nuestro sitio web. Google puede utilizar estos datos según su propia política de privacidad.
              </Typography>
              <Chip
                label="Más información"
                component="a"
                href="https://policies.google.com/privacy"
                target="_blank"
                clickable
                sx={{ bgcolor: Colors.primaryLight }}
              />
            </Paper>

            <Paper sx={{ p: 3, bgcolor: Colors.background }}>
              <Typography variant="h6" gutterBottom color={Colors.primaryDark}>
                🗺️ Google Maps
              </Typography>
              <Typography variant="body2" paragraph>
                Para mostrar mapas de ubicación del centro educativo. Google Maps puede establecer sus propias cookies.
              </Typography>
              <Chip
                label="Política de Google Maps"
                component="a"
                href="https://cloud.google.com/maps-platform/terms/"
                target="_blank"
                clickable
                sx={{ bgcolor: Colors.primaryLight }}
              />
            </Paper>

            <Paper sx={{ p: 3, bgcolor: Colors.background }}>
              <Typography variant="h6" gutterBottom color={Colors.primaryDark}>
                🔗 Redes Sociales
              </Typography>
              <Typography variant="body2" paragraph>
                Los botones de redes sociales pueden establecer cookies de las respectivas plataformas.
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip label="Facebook" sx={{ bgcolor: Colors.accent }} />
                <Chip label="Instagram" sx={{ bgcolor: Colors.accent }} />
                <Chip label="Twitter" sx={{ bgcolor: Colors.accent }} />
              </Stack>
            </Paper>
          </Stack>
        </Paper>

        {/* Gestión de Cookies */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: Colors.surface, border: `1px solid ${Colors.primaryLight}` }}>
          <Typography variant="h4" gutterBottom sx={{ color: Colors.primary, display: 'flex', alignItems: 'center' }}>
            <TuneIcon sx={{ mr: 2 }} />
            4. Cómo Gestionar las Cookies
          </Typography>
          <Divider sx={{ mb: 3, borderColor: Colors.primaryLight }} />

          <Typography paragraph>
            Puede gestionar las cookies de varias maneras:
          </Typography>

          <Stack spacing={3}>
            <Paper sx={{ p: 3, bgcolor: Colors.background }}>
              <Typography variant="h6" gutterBottom color={Colors.primaryDark}>
                🔧 A través de nuestro sitio web
              </Typography>
              <Typography variant="body2" paragraph>
                Utilice el panel de configuración de cookies en la parte superior de esta página para activar o desactivar categorías específicas de cookies.
              </Typography>
            </Paper>

            <Paper sx={{ p: 3, bgcolor: Colors.background }}>
              <Typography variant="h6" gutterBottom color={Colors.primaryDark}>
                🌐 A través de su navegador
              </Typography>
              <Typography variant="body2" paragraph>
                Todos los navegadores permiten gestionar las cookies. Consulte la ayuda de su navegador para más información:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                <Chip
                  label="Chrome"
                  component="a"
                  href="https://support.google.com/chrome/answer/95647"
                  target="_blank"
                  clickable
                  sx={{ bgcolor: Colors.primaryLight }}
                />
                <Chip
                  label="Firefox"
                  component="a"
                  href="https://support.mozilla.org/es/kb/habilitar-y-deshabilitar-cookies-sitios-web-rastrear-preferencias"
                  target="_blank"
                  clickable
                  sx={{ bgcolor: Colors.primaryLight }}
                />
                <Chip
                  label="Safari"
                  component="a"
                  href="https://support.apple.com/es-es/guide/safari/sfri11471/mac"
                  target="_blank"
                  clickable
                  sx={{ bgcolor: Colors.primaryLight }}
                />
                <Chip
                  label="Edge"
                  component="a"
                  href="https://support.microsoft.com/es-es/help/17442/windows-internet-explorer-delete-manage-cookies"
                  target="_blank"
                  clickable
                  sx={{ bgcolor: Colors.primaryLight }}
                />
              </Stack>
            </Paper>

            <Paper sx={{ p: 3, bgcolor: Colors.background }}>
              <Typography variant="h6" gutterBottom color={Colors.primaryDark}>
                🚫 Desactivar cookies analíticas
              </Typography>
              <Typography variant="body2" paragraph>
                Para desactivar específicamente Google Analytics:
              </Typography>
              <Chip
                label="Complemento de exclusión de Google Analytics"
                component="a"
                href="https://tools.google.com/dlpage/gaoptout"
                target="_blank"
                clickable
                sx={{ bgcolor: Colors.accent }}
              />
            </Paper>
          </Stack>

          <Alert severity="warning" sx={{ mt: 3 }}>
            <Typography variant="body2">
              <strong>Importante:</strong> Deshabilitar ciertas cookies puede afectar la funcionalidad del sitio web y su experiencia de usuario.
            </Typography>
          </Alert>
        </Paper>

        {/* Consentimiento */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: Colors.surface, border: `1px solid ${Colors.primaryLight}` }}>
          <Typography variant="h4" gutterBottom sx={{ color: Colors.primary, display: 'flex', alignItems: 'center' }}>
            <SecurityIcon sx={{ mr: 2 }} />
            5. Base Legal y Consentimiento
          </Typography>
          <Divider sx={{ mb: 3, borderColor: Colors.primaryLight }} />

          <Typography paragraph>
            El uso de cookies en nuestro sitio web se basa en:
          </Typography>

          <List>
            <ListItem>
              <ListItemText
                primary="Consentimiento del usuario"
                secondary="Para cookies no esenciales, solicitamos su consentimiento explícito"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Interés legítimo"
                secondary="Para cookies técnicas necesarias para el funcionamiento del sitio"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Cumplimiento legal"
                secondary="Según lo establecido en la normativa de cookies (Directiva 2009/136/CE)"
              />
            </ListItem>
          </List>

          <Box sx={{ bgcolor: Colors.background, p: 3, borderRadius: 2, mt: 3 }}>
            <Typography variant="h6" gutterBottom color={Colors.primaryDark}>
              Sus Derechos
            </Typography>
            <Typography variant="body2">
              • Derecho a retirar el consentimiento en cualquier momento<br/>
              • Derecho a configurar sus preferencias de cookies<br/>
              • Derecho a ser informado sobre el uso de cookies<br/>
              • Derecho a solicitar información sobre los datos recopilados
            </Typography>
          </Box>
        </Paper>

        {/* Actualizaciones */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: Colors.surface, border: `1px solid ${Colors.primaryLight}` }}>
          <Typography variant="h4" gutterBottom sx={{ color: Colors.primary, display: 'flex', alignItems: 'center' }}>
            <InfoIcon sx={{ mr: 2 }} />
            6. Actualizaciones de esta Política
          </Typography>
          <Divider sx={{ mb: 3, borderColor: Colors.primaryLight }} />

          <Typography paragraph>
            Esta política de cookies puede ser actualizada periódicamente para reflejar cambios en nuestras prácticas o por otros motivos legales, operacionales o regulatorios.
          </Typography>

          <Typography paragraph>
            Le recomendamos revisar esta página regularmente para mantenerse informado sobre nuestro uso de cookies y tecnologías relacionadas.
          </Typography>

          <Alert severity="info">
            <Typography variant="body2">
              <strong>Notificaciones:</strong> En caso de cambios significativos, le notificaremos a través de un aviso destacado en nuestro sitio web o por email si tenemos su dirección de correo electrónico.
            </Typography>
          </Alert>
        </Paper>

        {/* Contacto */}
        <Paper sx={{ p: 4, mb: 4, bgcolor: Colors.surface, border: `1px solid ${Colors.primaryLight}` }}>
          <Typography variant="h4" gutterBottom sx={{ color: Colors.primary, display: 'flex', alignItems: 'center' }}>
            <CookieIcon sx={{ mr: 2 }} />
            7. Contacto sobre Cookies
          </Typography>
          <Divider sx={{ mb: 3, borderColor: Colors.primaryLight }} />

          <Typography paragraph>
            Si tiene preguntas sobre nuestra política de cookies o desea ejercer sus derechos:
          </Typography>

          <Box sx={{ bgcolor: Colors.background, p: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom color={Colors.primaryDark}>
              📧 Contacto sobre Cookies
            </Typography>
            <Typography><strong>Email:</strong> ampadelbosc@gmail.com</Typography>
            <Typography><strong>Email general:</strong> ampadelbosc@gmail.com</Typography>
            <Typography><strong>Teléfono:</strong> [Teléfono de contacto]</Typography>
            <Typography><strong>Dirección:</strong> [Dirección postal completa]</Typography>
            <Typography><strong>Horario:</strong> Lunes a viernes de 9:00 a 17:00</Typography>
          </Box>
        </Paper>

        {/* Footer */}
        <Paper sx={{ p: 3, bgcolor: Colors.primaryLight, textAlign: 'center' }}>
          <Typography variant="body2" color={Colors.primaryDark}>
            Esta Política de Cookies forma parte de nuestra Política de Privacidad.<br/>
            Al continuar navegando por nuestro sitio web, acepta el uso de cookies según se describe en esta política.<br/>
            <strong>Última actualización:</strong> {new Date().toLocaleDateString('es-ES')}
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default CookiesPolicy;